import { useMemo, useState } from "react";
import { Tag, Check, X, Search } from "lucide-react";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "../../../../components/UI/Popover";

const withAlpha = (hex, alphaHex) => {
  if (!hex || typeof hex !== "string") return undefined;
  const normalized = hex.startsWith("#") ? hex : `#${hex}`;
  if (normalized.length === 7) return `${normalized}${alphaHex}`;
  return normalized;
};

const TagZaloPicker = ({ tagZalos = [], currentUid, onAddTag, onRemoveTag }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const currentTag = useMemo(
    () => tagZalos.find((t) => t.conversations?.includes(currentUid)) || null,
    [tagZalos, currentUid],
  );

  const filteredTags = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return tagZalos;
    return tagZalos.filter((t) => (t.text || "").toLowerCase().includes(q));
  }, [tagZalos, query]);

  const handlePick = (tag) => {
    if (currentTag && currentTag._id === tag._id) {
      onRemoveTag(tag);
    } else {
      onAddTag(tag);
    }
    setOpen(false);
    setQuery("");
  };

  const handleClearCurrent = () => {
    if (currentTag) onRemoveTag(currentTag);
    setOpen(false);
    setQuery("");
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setQuery("");
      }}
    >
      <PopoverTrigger asChild>
        {currentTag ? (
          <button
            type="button"
            title="Đổi tag"
            className="flex items-center gap-1 rounded-full px-2 py-[2px] text-[11px] font-medium border border-transparent hover:border-gray-200 transition"
            style={{
              backgroundColor: withAlpha(currentTag.color, "26"),
              color: currentTag.color,
            }}
          >
            <span
              className="inline-block w-2 h-2 rounded-full"
              style={{ backgroundColor: currentTag.color }}
            />
            <span className="max-w-[140px] truncate">{currentTag.text}</span>
            <span
              role="button"
              aria-label="Gỡ tag"
              title="Gỡ tag"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onRemoveTag(currentTag);
              }}
              className="ml-0.5 p-0.5 rounded-full hover:bg-black/10"
            >
              <X size={11} />
            </span>
          </button>
        ) : (
          <button
            type="button"
            title="Gắn tag Zalo"
            className="flex items-center gap-1 rounded-full px-2 py-[2px] text-[11px] text-gray-500 border border-dashed border-gray-300 hover:border-gray-400 hover:text-gray-700 transition"
          >
            <Tag size={12} strokeWidth={1.8} />
            <span>Gắn tag Zalo</span>
          </button>
        )}
      </PopoverTrigger>

      <PopoverContent
        align="start"
        sideOffset={6}
        className="!w-64 !p-0 overflow-hidden"
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          const input = e.currentTarget.querySelector("input[data-tagpicker-search]");
          if (input) input.focus();
        }}
      >
        <div className="px-3 pt-3 pb-2 text-sm font-medium text-gray-800">
          Gắn tag Zalo cho cuộc trò chuyện
        </div>

        {tagZalos.length > 0 && (
          <div className="px-3 pb-2">
            <div className="relative">
              <Search
                size={14}
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400"
              />
              <input
                data-tagpicker-search
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Tìm tag…"
                className="w-full h-8 pl-8 pr-2 text-sm rounded-md border border-gray-200 focus:outline-none focus:border-gray-400"
              />
            </div>
          </div>
        )}

        <ul className="max-h-64 overflow-y-auto pb-1">
          {tagZalos.length === 0 ? (
            <li className="px-3 py-6 text-center text-xs text-gray-400">
              Shop chưa có tag nào
            </li>
          ) : filteredTags.length === 0 ? (
            <li className="px-3 py-6 text-center text-xs text-gray-400">
              Không tìm thấy tag khớp
            </li>
          ) : (
            filteredTags.map((tag) => {
              const isCurrent = currentTag && currentTag._id === tag._id;
              return (
                <li key={tag._id}>
                  <button
                    type="button"
                    onClick={() => handlePick(tag)}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-gray-50 ${
                      isCurrent ? "bg-gray-50" : ""
                    }`}
                  >
                    <span
                      className="inline-block w-3 h-3 rounded-full shrink-0"
                      style={{ backgroundColor: tag.color }}
                    />
                    <span className="flex-1 truncate text-gray-800">
                      {tag.text}
                    </span>
                    {isCurrent && (
                      <Check size={16} className="text-gray-600 shrink-0" />
                    )}
                  </button>
                </li>
              );
            })
          )}
        </ul>

        {currentTag && (
          <div className="border-t border-gray-100">
            <button
              type="button"
              onClick={handleClearCurrent}
              className="w-full px-3 py-2 text-sm text-left text-red-600 hover:bg-red-50"
            >
              Bỏ tag khỏi cuộc trò chuyện
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default TagZaloPicker;
